import { useContext, useEffect, useMemo, useState } from 'react';

import { faCircleInfo } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import {
  Accordion,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/videa-components/src/index';
import { useAtomValue } from 'jotai';
import { useParams } from 'react-router-dom';

import { ExternalPatientContext } from '@/app/external/constants';
import { selectTeethAtom } from '@/modules/patientList/atoms';
import { QuadrantPerioIndicator } from '@/modules/patientList/patientStrip/findings/findingChip';
import type { TDisplayOrderQuadrantAbbreviations } from '@/modules/patientList/patientStrip/patientStripOdontogram/types';
import { DisplayOrderQuadrantAbbreviations } from '@/modules/patientList/patientStrip/patientStripOdontogram/types';
import type { TVisitSummary } from '@/react-query/useVisitSummaries';
import { useRblSeverities } from '@/utils/hooks/useRblSeverities';
import { useUniversalToFDI } from '@/utils/internationalization/useUniversalToFDI';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import type { ImageObject } from '../types';

import { BoneLevelAccordionItem } from './BoneLevelAccordionItem';
import {
  getTeethFilteredByImageView,
  getToothHasBoneLevelMeasurements,
  getVisitSidebarView,
  useVisitSummaryTeethByQuadrants,
} from './helpers';
import { SeverityTag } from './SeverityTag';

interface Props {
  activeImage?: ImageObject;
  estimatedVisitId?: number | null;
  isLoading: boolean;
  teeth: TVisitSummary['teeth'];
  unifiedAppointmentId?: number;
  visitSummary?: TVisitSummary;
}

export const BoneLevel = ({
  activeImage,
  estimatedVisitId,
  isLoading,
  teeth,
  unifiedAppointmentId,
  visitSummary,
}: Props) => {
  const [isShowMeasurements, setIsShowMeasurements] = useState(false);
  const [accordionValues, setAccordionValues] = useState<
    TDisplayOrderQuadrantAbbreviations[]
  >(DisplayOrderQuadrantAbbreviations.map((value) => value));
  const selectedTeeth = useAtomValue(selectTeethAtom);

  const { displayLocalizedQuadrantShortName } = useUniversalToFDI();

  const severityStrings = useRblSeverities(visitSummary, true);
  const hasNonNormal = severityStrings?.some((s) => s !== 'normal');
  const { trackEvent } = useTrackEvent();
  const {
    isIframe,
    externalPatientId,
    externalImageId,
    patientId,
    trialStatus,
    centralizedBilling: isCentralizedBilling,
  } = useContext(ExternalPatientContext);
  const { id: selectedPatientId } = useParams<{ id: string }>();

  const visitSummaryTeethByQuadrants = useVisitSummaryTeethByQuadrants(teeth);

  // If applicable, filter out teeth that aren't identified in the single
  // image or aren't selected.
  const boneLevelQuadrants = useMemo(() => {
    const defaultValue = {} as Record<
      TDisplayOrderQuadrantAbbreviations,
      TVisitSummary['teeth']
    >;
    // Using reduce rather than mapping Object.entries() to maintain the type
    // of the quadrant keys.
    return DisplayOrderQuadrantAbbreviations.reduce((acc, quadrant) => {
      const quadrantTeeth = visitSummaryTeethByQuadrants[quadrant];

      const filteredTeeth = getTeethFilteredByImageView(
        quadrantTeeth,
        activeImage,
        selectedTeeth,
      );

      return { ...acc, [quadrant]: filteredTeeth };
    }, defaultValue);
  }, [visitSummaryTeethByQuadrants, activeImage, selectedTeeth]);

  const hasBoneLevelMeasurements = useMemo(() => {
    return Object.values(boneLevelQuadrants).some((quadrant) =>
      quadrant.some((tooth) => getToothHasBoneLevelMeasurements(tooth)),
    );
  }, [boneLevelQuadrants]);

  const handleMeasurementsToggle = () => {
    const isNewShowMeasurements = !isShowMeasurements;
    trackEvent(mixpanelEvents.visitSidebarBoneLevelToggle, {
      isOpen: isNewShowMeasurements,
      estimatedVisitId,
      unifiedAppointmentId,
      view: getVisitSidebarView(activeImage, selectedTeeth),
      isIframe: !!isIframe,
      externalPatientId,
      externalImageId,
      patientId: selectedPatientId ?? patientId,
      imageId: activeImage?.imageId,
      trialStatus,
      centralizedBilling: isCentralizedBilling,
    });
    setIsShowMeasurements(isNewShowMeasurements);
  };

  const handleAccordionChange = (
    newItems: TDisplayOrderQuadrantAbbreviations[],
  ) => {
    const opened = newItems.find((item) => !accordionValues.includes(item));
    const closed = accordionValues.find((item) => !newItems.includes(item));

    const quadrant = opened ?? closed;
    const quadrantIndex = quadrant
      ? DisplayOrderQuadrantAbbreviations.indexOf(quadrant)
      : undefined;

    trackEvent(mixpanelEvents.visitSidebarBoneLevelAccordionToggle, {
      estimatedVisitId,
      isOpen: !!opened,
      quadrant,
      severity:
        typeof quadrantIndex === 'number'
          ? severityStrings?.[quadrantIndex]
          : undefined,
      unifiedAppointmentId,
      view: getVisitSidebarView(activeImage, selectedTeeth),
      isIframe: !!isIframe,
      externalPatientId,
      externalImageId,
      patientId: selectedPatientId ?? patientId,
      imageId: activeImage?.imageId,
      trialStatus,
      centralizedBilling: isCentralizedBilling,
    });

    setAccordionValues(newItems);
  };

  useEffect(() => {
    // When we change between FMX, single image view, and tooth selection, the
    // accordion items should always reopen.
    setAccordionValues(DisplayOrderQuadrantAbbreviations.map((value) => value));
  }, [activeImage?.imageId, selectedTeeth]);

  return (
    <div data-testid="bone-level">
      <div className="vai-mb-2 vai-flex vai-justify-between vai-items-center">
        <div className="vai-flex vai-items-center vai-gap-1">
          <p className="vai-font-inter vai-text-xs vai-text-zinc-300 vai-uppercase">
            <Trans>Bone Level</Trans>
          </p>
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <FontAwesomeIcon
                  icon={faCircleInfo}
                  className="vai-text-zinc-300 vai-text-xs"
                />
              </TooltipTrigger>
              <TooltipContent variant="dark" portal className="vai-max-w-xs">
                <p className="vai-text-sm [@media(max-width:310px)]:!vai-text-[12px] vai-leading-relaxed">
                  <Trans>
                    Bone level represents the most severe reading across all
                    images of the quadrant.
                  </Trans>
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        {hasBoneLevelMeasurements && !isLoading && (
          <button
            data-testid="bone-level-toggle"
            className="vai-font-inter vai-text-xs vai-text-zinc-300 vai-underline"
            onClick={handleMeasurementsToggle}
          >
            {isShowMeasurements ? (
              <Trans>Hide measurements</Trans>
            ) : (
              <Trans>Show measurements</Trans>
            )}
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="vai-w-full vai-flex vai-flex-col vai-gap-2">
          <Skeleton className="vai-w-[45%] vai-h-4" />
          <Skeleton className="vai-w-full vai-h-4" />
          <Skeleton className="vai-w-full vai-h-4" />
          <Skeleton className="vai-w-full vai-h-4" />
        </div>
      ) : (
        <>
          {!!severityStrings && (
            <>
              <div className="vai-flex vai-flex-row vai-items-start vai-gap-2">
                {hasNonNormal && (
                  <div className="vai-pt-0.5">
                    <QuadrantPerioIndicator
                      quadrantColors={severityStrings}
                      isScheduleViewEnabled={false}
                    />
                  </div>
                )}
                <div className="vai-flex vai-gap-2 vai-flex-wrap">
                  {DisplayOrderQuadrantAbbreviations.filter(
                    (quadrant) => boneLevelQuadrants[quadrant].length,
                  ).map((quadrant) => {
                    const quadrantIndex =
                      DisplayOrderQuadrantAbbreviations.indexOf(quadrant);

                    const severity = severityStrings?.[quadrantIndex];

                    return (
                      <div
                        data-testid={`bone-level-quadrant-${quadrant}`}
                        key={quadrant}
                        className="vai-flex vai-flex-row vai-items-start vai-gap-2"
                      >
                        <p className="vai-typography-small vai-text-white">
                          {displayLocalizedQuadrantShortName(quadrant)}
                        </p>
                        {hasNonNormal && <SeverityTag severity={severity} />}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
          {hasBoneLevelMeasurements && isShowMeasurements && (
            <Accordion
              className="vai-mt-2 vai-w-full vai-text-white"
              onValueChange={handleAccordionChange}
              type="multiple"
              value={accordionValues}
            >
              {DisplayOrderQuadrantAbbreviations.map((quadrant) => {
                const quadrantIndex =
                  DisplayOrderQuadrantAbbreviations.indexOf(quadrant);

                return (
                  <BoneLevelAccordionItem
                    key={quadrant}
                    quadrant={quadrant}
                    quadrantTeeth={boneLevelQuadrants[quadrant]}
                    severity={severityStrings?.[quadrantIndex]}
                    activeImage={activeImage}
                  />
                );
              })}
            </Accordion>
          )}
          {!hasBoneLevelMeasurements && (
            <p className="vai-font-inter vai-text-sm vai-text-white !vai-mt-2">
              <Trans>No measurements available</Trans>
            </p>
          )}
        </>
      )}
    </div>
  );
};
